import type {
  AuthenticationResult,
  IPublicClientApplication,
  PopupRequest,
} from "@azure/msal-browser";
import { loginRequest } from "../../authConfig";
import { fetchRegistrationAuthorisation } from "./registrationService";

const REAUTH_FLAG_KEY = "SS_REQUIRE_REAUTH";

const shouldForceReauth = (): boolean => {
  try {
    return localStorage.getItem(REAUTH_FLAG_KEY) === "1"; // A logout action set this flag when we must show the credential prompt.
  } catch {
    return false;
  }
};

const clearForceReauthFlag = () => {
  try {
    localStorage.removeItem(REAUTH_FLAG_KEY); // After we honour the flag, clear it so normal silent SSO resumes next time.
  } catch {
    // Best effort: storage may be unavailable (e.g. blocked in the webview).
  }
};

/**
 * Initiates the MSAL popup login flow and returns the full AuthenticationResult.
 * Keeping this logic in a shared helper ensures every UI flow honours the reauth flag
 * and scope configuration without duplicating code.
 */
export const startPopupLogin = async (
  msalInstance: IPublicClientApplication,
  overrides?: Partial<PopupRequest>
): Promise<AuthenticationResult> => {
  const forceReauth = shouldForceReauth();

  const request: PopupRequest = {
    ...loginRequest,
    prompt: forceReauth ? "login" : "select_account",
    ...overrides,
  };

  const result = await msalInstance.loginPopup(request);
  if (forceReauth) {
    clearForceReauthFlag();
  }
  return result;
};

/**
 * Checks registration status with the backend.
 * This is a read-only check to see if the user is authorized.
 */
export const checkRegistrationStatus = async (accessToken: string): Promise<any> => {
  // This function acts as the "Handshake" with the backend.
  // We delegate to the registration service to hit the read-only /authorisation/status endpoint.
  return await fetchRegistrationAuthorisation(accessToken);
};

export interface GetAccessTokenOptions {
  forceRefresh?: boolean;
}

/**
 * Acquires an access token for the backend API.
 * @param msalInstance The MSAL instance.
 * @returns The access token.
 */
export const getAccessToken = async (
  msalInstance: IPublicClientApplication,
  options: GetAccessTokenOptions = {}
): Promise<string> => {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    throw new Error("No accounts found. User might be signed out.");
  }

  const forceReauth = shouldForceReauth();
  const forceRefresh = forceReauth || options.forceRefresh === true;

  const request = {
    scopes: loginRequest.scopes,
    account: accounts[0],
    forceRefresh,
  };

  try {
    if (!forceReauth) {
      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    }
    throw new Error("Skipping silent acquisition due to reauth requirement."); // Force the catch block to execute the popup path.
  } catch (_error) {
    if (forceReauth) {
      clearForceReauthFlag();
    }
    const response = await msalInstance.acquireTokenPopup({
      ...request,
      prompt: forceReauth ? "login" : undefined, // When reauth is required we demand credentials; otherwise we allow cached SSO.
    } as any);
    return response.accessToken;
  }
};
